import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Euro, Calendar, AlertCircle, ClipboardCheck, Ban, Users, Mail, Phone,
  CheckCircle2, AlertTriangle, Info,
} from 'lucide-react';

export default function AnalysisPanel({ analysis }) {
  if (!analysis) {
    return (
      <Card>
        <CardContent className="pt-10 pb-10 text-center">
          <Info className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">Brak analizy NLP</p>
          <p className="text-xs text-muted-foreground mt-1">
            Kliknij "Analizuj" aby uruchomić ekstrakcję Claude Sonnet
          </p>
        </CardContent>
      </Card>
    );
  }

  const fmtMoney = (v, c = 'PLN') => {
    if (!v) return '—';
    return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' ' + c;
  };

  return (
    <div className="space-y-4">
      {/* Summary + meta */}
      {analysis.raw_extraction?.summary_pl && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Streszczenie</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{analysis.raw_extraction.summary_pl}</p>
            <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
              <span>Model:</span>
              <Badge variant="outline">{analysis.model_used}</Badge>
              {analysis.raw_extraction.confidence != null && (
                <>
                  <span className="ml-2">Pewność:</span>
                  <Badge variant="outline">{Math.round((analysis.raw_extraction.confidence || 0) * 100)}%</Badge>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kluczowe liczby */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Euro className="w-3 h-3" /> Wadium
            </div>
            <div className="text-lg font-semibold">
              {fmtMoney(analysis.wadium_amount, analysis.wadium_currency)}
            </div>
            {analysis.wadium_form && (
              <div className="text-xs text-muted-foreground mt-0.5">{analysis.wadium_form}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Euro className="w-3 h-3" /> Wartość z SWZ
            </div>
            <div className="text-lg font-semibold">
              {fmtMoney(analysis.estimated_value_from_swz)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <Calendar className="w-3 h-3" /> Termin ofert
            </div>
            <div className="text-base font-semibold">
              {analysis.deadline_submission || '—'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
              <ClipboardCheck className="w-3 h-3" /> Kryteriów
            </div>
            <div className="text-lg font-semibold">
              {analysis.evaluation_criteria?.length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Red flags */}
      {analysis.consilium_red_flags?.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              Red flags ({analysis.consilium_red_flags.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {analysis.consilium_red_flags.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Kryteria oceny */}
      {analysis.evaluation_criteria?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Kryteria oceny</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analysis.evaluation_criteria.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 pb-1.5">
                  <div>
                    <span className="font-medium">{c.name || c}</span>
                    {c.type && <span className="ml-2 text-xs text-muted-foreground">({c.type})</span>}
                  </div>
                  {c.weight && (
                    <Badge variant="outline">{c.weight}%</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wymagania udziału */}
      {analysis.participation_requirements?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4" />
              Warunki udziału
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {analysis.participation_requirements.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  <div>
                    {r.type && <Badge variant="outline" className="text-xs mr-2">{r.type}</Badge>}
                    <span>{r.description || r}</span>
                    {r.mandatory === false && (
                      <Badge variant="outline" className="text-xs ml-2 bg-muted">opcjonalne</Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Wykluczenia */}
      {analysis.exclusion_grounds?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Ban className="w-4 h-4" />
              Podstawy wykluczenia
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {analysis.exclusion_grounds.map((e, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {e}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CPV + kontakt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {analysis.cpv_codes_extracted?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">CPV wyekstraktowane</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1.5">
                {analysis.cpv_codes_extracted.map((c, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {(analysis.contact_person || analysis.contact_email || analysis.contact_phone) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Kontakt zamawiającego</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              {analysis.contact_person && (
                <div className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{analysis.contact_person}</span>
                </div>
              )}
              {analysis.contact_email && (
                <a
                  href={`mailto:${analysis.contact_email}`}
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {analysis.contact_email}
                </a>
              )}
              {analysis.contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{analysis.contact_phone}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
